import { useEffect, useState, useRef, useMemo, useContext } from 'react'
import { NavigationBar as UDSNavigationBar, Spacer, FlexGrid, useResponsiveProp } from '@telus-uds/components-web'
import { SpacerProps } from '../Spacer/Spacer'
import useKoodoAnalyticsEvent from '../../../../hooks/useKoodoAnalyticsEvent'
import { renderSpacer } from 'src/siteBuilder/utils/renderSpacer'
import { ContentfulAnalyticsEventType } from 'src/siteBuilder/utils/types/analytics'
import { ResponsiveProps } from '../../../utils/types/components'
import { NavigationBarStickyContainer, NavigationBarContainer } from './Styles'
import { useNavigationBarAnalytics } from '../../../../hooks/useNavigationBarAnalytics'
import { SiteBuilderContext } from '../../../renderer/context/SiteBuilderContext'

type ResponsiveFlexGridOptions = {
  xs?: boolean
  sm?: boolean
  md?: boolean
  lg?: boolean
  xl?: boolean
}

export type NavigationBarProps = {
  heading?: string
  headingLevel?: string
  items: NavigationBarItemProps[]
  selectedId: string
  spacer?: SpacerProps
  isNavigationSticky?: boolean
  stickyNavigationBackgroundColor?: string
  stickyNavigationVerticalPadding?: boolean
  stickyNavigationBottomBorder?: boolean
  toggleLimitWidth?: ResponsiveProps<ResponsiveFlexGridOptions>
}

type NavigationBarItemProps = {
  id: string
  label: string
  href?: string
  analyticsEvent?: ContentfulAnalyticsEventType
  items?: NavigationBarSubItemProps[]
  onClick?: () => void
}

type NavigationBarSubItemProps = {
  id: string
  label: string
  href: string
  analyticsEvent?: ContentfulAnalyticsEventType
  onClick?: () => void
}

const NavigationBar = ({
  heading,
  headingLevel,
  items,
  selectedId,
  spacer,
  isNavigationSticky = false,
  stickyNavigationBackgroundColor = 'athens_grey',
  stickyNavigationVerticalPadding = false,
  stickyNavigationBottomBorder = false,
  toggleLimitWidth,
}: NavigationBarProps) => {
  const [isAnalyticsTriggered, setIsAnalyticsTriggered] = useState(false)
  const [idSelected, setIdSelected] = useState<string>(selectedId)
  const [analytics, setAnalytics] = useState<NavigationBarSubItemProps['analyticsEvent']>(null)
  const [isInView, setIsInView] = useState(true)
  const navBarRef = useRef<HTMLDivElement>(null)
  const { brand } = useContext(SiteBuilderContext)

  // Detect if mobile viewport for Adobe Analytics
  const isMobile = useResponsiveProp({ xs: true, sm: true, md: false, lg: false, xl: false })

  /**
   * ANALYTICS: Modify navigation URLs with linktype parameter for analytics tracking
   *
   * This ensures consistent analytics tracking across devices by setting:
   * - Desktop: linktype=ge-subnav
   * - Mobile: linktype=ge-mobilesubnav
   *
   * Conditions:
   * - Only runs on telus.com domains
   * - Only modifies URLs that already contain a linktype parameter
   * - Preserves all other URL parameters unchanged
   *
   * Optimization: Only recalculates when items change or when crossing mobile/desktop boundary,
   * not on every resize event within the same breakpoint.
   */
  const navigationItems = useMemo(() => {
    if (brand !== 'telus') {
      return items.map((item) => ({
        ...item,
        // Set href to undefined if item has sub-items to prevent parent clicks from being tracked
        href: item.items && item.items.length > 0 ? undefined : item.href,
      }))
    }

    const currentIsMobile = isMobile === true
    const linkTypeValue = currentIsMobile ? 'ge-mobilesubnav' : 'ge-subnav'

    return items.map((item) => {
      // Set href to undefined if item has sub-items to prevent parent clicks from being tracked
      const hasSubItems = item.items && item.items.length > 0
      const modifiedHref = hasSubItems
        ? undefined
        : item.href?.includes('linktype=')
        ? item.href.replace(/linktype=[^&#]*/, `linktype=${linkTypeValue}`)
        : item.href

      return {
        ...item,
        href: modifiedHref,
        items: item.items?.map((subItem) => ({
          ...subItem,
          href: subItem.href?.includes('linktype=')
            ? subItem.href.replace(/linktype=[^&#]*/, `linktype=${linkTypeValue}`)
            : subItem.href,
        })),
      }
    })
  }, [items, isMobile, brand])

  // Analytics: Fires Adobe Analytics directly and returns Koodo event data
  const { handleNavigationChange } = useNavigationBarAnalytics(navigationItems, isMobile, brand)

  useKoodoAnalyticsEvent(analytics, isAnalyticsTriggered)

  useEffect(() => setIsAnalyticsTriggered(true), [analytics])

  useEffect(() => {
    const currentRef = navBarRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only show sticky nav when scrolled past the bottom of the component
        setIsInView(!entry.isIntersecting && entry.boundingClientRect.bottom < 0)
      },
      { threshold: 0 }
    )

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [])

  useEffect(() => {
    //! If the page reloads, get the hash from the URL and set the selectedId
    const hash = window?.location?.hash
    if (hash) {
      items?.forEach((item) => {
        if (item.href === hash) {
          setIdSelected(item.id)
        }
      })
    }
  }, [items])

  const onChangeSelectedId = (id: string) => {
    setIdSelected(id)

    // Fire Analytics and get Koodo analytics event
    const analyticsEvent = handleNavigationChange(id)

    if (analyticsEvent) {
      setAnalytics(analyticsEvent)
    }
  }

  const responsiveToggleLimitWidth = useResponsiveProp({
    xs: false,
    sm: false,
    md: false,
    lg: toggleLimitWidth || false,
    xl: toggleLimitWidth,
  })

  const responsiveToggleGutter = useResponsiveProp({
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: false,
  })

  const responsiveOutsideGutter = useResponsiveProp({
    xs: false,
    sm: false,
    md: false,
    lg: false,
    xl: true,
  })

  // TODO: We need to look into UDS adding the stick navigation into Navigartion Bar to avoid creating complexity to this component
  const backgroundColor = stickyNavigationBackgroundColor === 'athens_grey' ? '#f4f4f7' : '#ffffff'

  return (
    <>
      <div data-testid="navigation-bar-testid" data-navigation-container ref={navBarRef}>
        {renderSpacer(spacer)}
        <UDSNavigationBar
          heading={heading}
          headingLevel={headingLevel}
          items={navigationItems}
          selectedId={idSelected}
          onChange={onChangeSelectedId}
        />
      </div>

      {isInView && isNavigationSticky && (
        <NavigationBarStickyContainer
          isVisible={isInView}
          backgroundColor={backgroundColor}
          verticalPadding={stickyNavigationVerticalPadding}
          bottomBorder={stickyNavigationBottomBorder}
          data-testid="sticky-navigation-bar-testid"
        >
          <Spacer space={{ xs: 3, lg: 11 }} direction="row" />
          <NavigationBarContainer
            isVisible={isInView}
            backgroundColor={backgroundColor}
            verticalPadding={stickyNavigationVerticalPadding}
            bottomBorder={stickyNavigationBottomBorder}
            data-navigation-container
          >
            {renderSpacer(spacer)}
            <FlexGrid
              limitWidth={responsiveToggleLimitWidth}
              gutters={responsiveToggleGutter}
              outsideGutter={responsiveOutsideGutter}
            >
              <FlexGrid.Row>
                <FlexGrid.Col>
                  <UDSNavigationBar
                    heading={heading}
                    headingLevel={headingLevel}
                    items={navigationItems}
                    selectedId={idSelected}
                    onChange={onChangeSelectedId}
                  />
                </FlexGrid.Col>
              </FlexGrid.Row>
            </FlexGrid>
          </NavigationBarContainer>
          <Spacer space={{ xs: 3, lg: 11 }} direction="row" />
        </NavigationBarStickyContainer>
      )}
    </>
  )
}

export default NavigationBar
